/**
 * Màn khách xem album.
 *
 * Khách mở trên điện thoại, giữa lúc bận, và không có tài khoản nào để đăng
 * nhập. Nên: chữ to, một cột trên máy nhỏ, và mọi lỗi nói bằng câu người
 * thường đọc được kèm việc cần làm tiếp — không mã lỗi, không thuật ngữ.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PhotoMasonry } from "@/components/photo-grid/PhotoMasonry";
import { Clock, Download, ImageOff, ShieldAlert } from "lucide-react";

import { Button } from "@/ui/button";
import { Skeleton } from "@/ui/skeleton";
import {
  albumShareKeys,
  downloadCustomerAlbumZip,
  openCustomerAlbum,
  openCustomerOrderAlbum,
  openCustomerOrderOriginalAlbum,
  type CustomerAlbum,
} from "./api";

type CustomerAlbumPageProps =
  | { token: string; orderId?: never; source?: never }
  | { orderId: string; token?: never; source?: "final" | "original" };

export function CustomerAlbumPage({ token, orderId, source = "final" }: CustomerAlbumPageProps) {
  const hasSource = Boolean(token || orderId);
  const isOriginal = orderId !== undefined && source === "original";
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const albumQ = useQuery({
    queryKey: token
      ? albumShareKeys.customer(token)
      : isOriginal
        ? albumShareKeys.customerOrderOriginal(orderId ?? "")
        : albumShareKeys.customerOrder(orderId ?? ""),
    queryFn: () =>
      token
        ? openCustomerAlbum(token)
        : isOriginal
          ? openCustomerOrderOriginalAlbum(orderId ?? "")
          : openCustomerOrderAlbum(orderId ?? ""),
    enabled: hasSource,
    retry: false,
  });

  if (!hasSource) {
    return (
      <AlbumMessage
        title="Thiếu link"
        detail="Chị mở lại trang booking hoặc đường link studio đã gửi giúp em ạ."
      />
    );
  }
  if (albumQ.isLoading) return <AlbumSkeleton />;
  if (albumQ.isError) {
    return <AlbumMessage title="Không mở được album" detail={(albumQ.error as Error).message} />;
  }

  const album = albumQ.data as CustomerAlbum;
  const zipFilename = archiveFilename(album.orderCode, isOriginal ? "goc" : "anh");
  if (album.photos.length === 0) {
    return (
      <AlbumMessage
        title={isOriginal ? "Ảnh gốc chưa sẵn sàng" : "Ảnh đang được hoàn thiện"}
        detail={
          isOriginal
            ? "Studio sẽ cập nhật sau khi ảnh gốc được tải lên ạ."
            : "Studio sẽ báo chị ngay khi ảnh sẵn sàng ạ."
        }
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-6">
      <header className="mb-5 space-y-1">
        <h1 className="text-2xl font-semibold md:text-3xl">
          {isOriginal ? "Ảnh gốc" : "Ảnh"} của {album.customerName}
        </h1>
        <p className="text-sm text-muted-foreground">
          Đơn {album.orderCode} · {album.photos.length} ảnh
          {isOriginal ? null : <> · Link xem đến {formatDate(album.expiresAt)}</>}
        </p>
      </header>

      {/* Ảnh gốc: nói thẳng hai điều khách cần biết trước khi cuộn xuống — ảnh
          đang xem là bản xem thử, và cửa sổ xem có hạn. Giấu hai điều này thì
          khách tưởng đây là ảnh mình đã mua, rồi quay lại hỏi vì sao ảnh mờ và
          vì sao hôm sau không mở được nữa. */}
      {isOriginal ? (
        <div className="mb-5 space-y-2 rounded-lg border border-amber-300/70 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100">
          <p className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              Đây là <strong>ảnh xem thử chất lượng thấp</strong>, có đóng chữ chìm và không tải về
              được. Ảnh sắc nét, sạch chữ chìm sẽ được giao sau khi chị chọn và studio sửa xong ạ.
            </span>
          </p>
          <p className="flex items-start gap-2">
            <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              Chị xem và chọn giúp em trước <strong>{formatDateTime(album.expiresAt)}</strong>. Quá
              hạn mà chị chưa chọn xong thì nhắn studio mở lại giúp ạ.
            </span>
          </p>
        </div>
      ) : null}

      {/* Lưới dùng chung với mười màn kia: ô ôm ĐÚNG KHỔ từng tấm. Trước
          26/09/2026 màn này ép mọi tấm về khổ 3:4 — khách mở album ra xem
          chính ảnh của mình mà thấy nó bị xén.

          Nút "Tải" nằm ở lớp phủ góc dưới thay vì dải chữ dưới ô: dải chữ đó
          chiếm chỗ của lưới và là đúng thứ CEO bảo bỏ. */}
      <PhotoMasonry
        label="Ảnh hoàn thiện"
        tiles={album.photos.map((p) => ({
          // Tấm ảnh thẻ nhiều cỡ (QĐ 0285) giao nhiều mục CÙNG photoId — khoá
          // theo (tấm, cỡ), không thì React gộp nhầm hai cỡ thành một ô.
          id: p.sizeLabel ? `${p.photoId}:${p.sizeLabel}` : p.photoId,
          // Bản vừa cho ô (CEO chốt 1a 30/09/2026); máy chủ cũ chưa trả thì lùi về `url`.
          url: p.gridUrl || p.url,
          width: p.width ?? null,
          height: p.height ?? null,
          flags: [],
          caption: p.sizeLabel ? `Ảnh ${p.seq} · ${p.sizeLabel}` : `Ảnh ${p.seq}`,
          label: p.sizeLabel ? `Ảnh ${p.seq} · cỡ ${p.sizeLabel}` : `Ảnh ${p.seq}`,
          // Màn này chỉ để XEM và TẢI — không có gì mở ra thêm.
          interactive: false,
          /* Tải về phải là bản đầy đủ, không phải ảnh đang hiện: khách lưu
             về để in hoặc gửi đi, bản xem trước dùng không được.

             `download` ở đây gần như trang trí: link ký nằm ở origin của kho
             ảnh, mà thuộc tính này thì trình duyệt bỏ qua khi khác origin.
             Thứ thật sự bắt trình duyệt LƯU thay vì MỞ RA XEM là header
             Content-Disposition máy chủ ký kèm vào `downloadUrl`. Đổi bên
             máy chủ sang link xem là màn này lặng lẽ quay về mở ảnh, không
             có gì báo.

             Ảnh GỐC không có `downloadUrl` (máy chủ trả rỗng từ quyết định
             0106) — nên điều kiện bám vào CHÍNH dữ liệu chứ không bám vào cờ
             `isOriginal` của màn: nếu sau này có bộ ảnh khác cũng không bán
             rời, nút tự biến mất mà không ai phải nhớ sửa chỗ này. */
          ...(p.downloadUrl
            ? {
                overlay: (
                  <a
                    href={p.downloadUrl}
                    download={p.sizeLabel ? `anh-${p.seq}-${p.sizeLabel}.jpg` : `anh-${p.seq}.jpg`}
                    className="absolute bottom-1.5 right-1.5 inline-flex min-h-[44px] items-center gap-1 rounded-full bg-black/55 px-3 text-sm font-medium text-white"
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    Tải
                  </a>
                ),
              }
            : {}),
        }))}
        onOpen={() => {}}
        emptyText="Album chưa có ảnh nào."
      />

      {/* Tải cả bộ bằng một file zip stream từ máy chủ: khách không bị trình
          duyệt chặn nhiều lượt tải rời rạc, còn backend không cần gom cả album
          vào RAM.

          Ảnh gốc không có khối này: cửa .zip của nó đã gỡ ở máy chủ. */}
      {isOriginal ? null : (
        <>
          <div className="mt-6 flex justify-center">
            <Button
              type="button"
              size="lg"
              disabled={downloadBusy}
              onClick={() => {
                if (token) {
                  void downloadArchive({
                    token,
                    filename: zipFilename,
                    setBusy: setDownloadBusy,
                    setError: setDownloadError,
                  });
                } else if (orderId) {
                  void downloadArchive({
                    orderId,
                    source,
                    filename: zipFilename,
                    setBusy: setDownloadBusy,
                    setError: setDownloadError,
                  });
                }
              }}
              className="min-h-[48px]"
            >
              <Download className="mr-2 h-5 w-5" aria-hidden />
              {downloadBusy ? "Đang nén..." : `Tải tất cả ${album.photos.length} ảnh`}
            </Button>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Album sẽ tải về thành một file .zip.
          </p>
          {downloadError ? (
            <p className="mt-2 text-center text-sm text-destructive">{downloadError}</p>
          ) : null}
        </>
      )}
    </div>
  );
}

async function downloadArchive(
  input: {
    filename: string;
    setBusy: (busy: boolean) => void;
    setError: (message: string | null) => void;
  } & (
    | { token: string; orderId?: never; source?: never }
    | { orderId: string; token?: never; source: "final" | "original" }
  ),
): Promise<void> {
  input.setBusy(true);
  input.setError(null);
  try {
    if ("token" in input) {
      await downloadCustomerAlbumZip({ token: input.token, filename: input.filename });
    } else {
      await downloadCustomerAlbumZip({
        orderId: input.orderId,
        source: input.source,
        filename: input.filename,
      });
    }
  } catch (err) {
    input.setError(
      err instanceof Error ? err.message : "Không tải được album, chị thử lại giúp em ạ.",
    );
  } finally {
    input.setBusy(false);
  }
}

function archiveFilename(orderCode: string, kind: "anh" | "goc"): string {
  const safeOrder = orderCode.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
  return `${safeOrder || "album"}-${kind}.zip`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("vi-VN");
}

/** Hạn xem ảnh gốc tính bằng GIỜ, nên chỉ ngày là thiếu: khách mở lúc 22h thì
 *  "đến 10/09" và "đến 10/09 22:00" là hai câu chuyện khác nhau. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })} ngày ${d.toLocaleDateString("vi-VN")}`;
}

function AlbumMessage({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
      <ImageOff className="h-12 w-12 text-muted-foreground" aria-hidden />
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="text-muted-foreground">{detail}</p>
    </div>
  );
}

function AlbumSkeleton() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-6">
      <Skeleton className="mb-5 h-9 w-64" />
      {/* Khung chờ so le chứ KHÔNG cùng một khổ: lưới thật ôm đúng khổ từng
          tấm, nên khung chờ vuông vắn đều tăm tắp là hứa một bố cục rồi đổi
          ngay khi ảnh về. */}
      <div className="flex gap-[2px]">
        {[0, 1].map((lan) => (
          <div key={lan} className="flex min-w-0 flex-1 flex-col gap-[2px]">
            {[3 / 4, 4 / 3, 2 / 3, 1].map((tiLe, i) => (
              <Skeleton key={i} className="w-full" style={{ aspectRatio: String(tiLe) }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
